/**
 * Send an ERC-20 (USDC) refund directly from the admin's connected browser wallet.
 * Uses the injected window.ethereum provider (MetaMask / Coinbase Wallet extension
 * or mobile in-app browser). No server-side key, no hot wallet — admin signs in
 * their own wallet.
 *
 * Returns the tx hash. Does NOT wait for mining — caller can record the hash
 * immediately so the buyer can verify once it confirms.
 */

const USDC_CONTRACTS = {
  base:     '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  ethereum: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
};
const CHAIN_IDS = { base: 8453, ethereum: 1 };
const USDC_DECIMALS = 6;

const CHAIN_ADD_PARAMS = {
  base: {
    chainId: '0x2105',
    chainName: 'Base',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.base.org'],
    blockExplorerUrls: ['https://basescan.org'],
  },
  ethereum: {
    chainId: '0x1',
    chainName: 'Ethereum',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://eth.llamarpc.com'],
    blockExplorerUrls: ['https://etherscan.io'],
  },
};

function isAddress(a) {
  return typeof a === 'string' && /^0x[0-9a-fA-F]{40}$/.test(a);
}

function toUnits(amountHumanReadable, decimals) {
  // Convert "0.10" -> 100000n (6 decimals). String math to avoid float drift.
  const [whole, frac = ''] = String(amountHumanReadable).split('.');
  const fracPadded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const asString = whole.replace(/^0+/, '') + fracPadded;
  return BigInt(asString || '0');
}

function encodeErc20Transfer(to, amountUnitsBigInt) {
  // ERC-20 transfer(address,uint256) selector = keccak256("transfer(address,uint256)")[:4] = 0xa9059cbb
  const methodId = 'a9059cbb';
  const paddedTo = to.toLowerCase().replace(/^0x/, '').padStart(64, '0');
  const paddedAmount = amountUnitsBigInt.toString(16).padStart(64, '0');
  return '0x' + methodId + paddedTo + paddedAmount;
}

async function ensureChain(eth, chain) {
  const targetIdHex = '0x' + CHAIN_IDS[chain].toString(16);
  try {
    await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: targetIdHex }] });
  } catch (err) {
    // 4902 = chain not added to wallet
    if (err?.code === 4902 || /Unrecognized chain/i.test(err?.message || '')) {
      await eth.request({ method: 'wallet_addEthereumChain', params: [CHAIN_ADD_PARAMS[chain]] });
      await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: targetIdHex }] });
    } else {
      throw err;
    }
  }
}

export async function sendUsdcRefund({ to, amountUsd, chain }) {
  if (typeof window === 'undefined' || !window.ethereum) {
    throw new Error('No browser wallet detected. Install MetaMask or Coinbase Wallet.');
  }
  if (!isAddress(to)) {
    throw new Error('Invalid buyer wallet address.');
  }
  if (!CHAIN_IDS[chain]) {
    throw new Error(`Unsupported chain: ${chain}`);
  }
  const amountFloat = Number(amountUsd);
  if (!Number.isFinite(amountFloat) || amountFloat <= 0) {
    throw new Error('Invalid refund amount.');
  }

  const eth = window.ethereum;
  // 1. Request accounts (prompts wallet unlock if needed)
  const accounts = await eth.request({ method: 'eth_requestAccounts' });
  const from = accounts[0];
  if (!from) throw new Error('No wallet account found.');

  // 2. Switch to the correct chain
  await ensureChain(eth, chain);

  // 3. Build the USDC transfer calldata
  const amountUnits = toUnits(amountUsd, USDC_DECIMALS);
  const data = encodeErc20Transfer(to, amountUnits);

  // 4. Send the transaction
  const txHash = await eth.request({
    method: 'eth_sendTransaction',
    params: [{
      from,
      to: USDC_CONTRACTS[chain],
      data,
      value: '0x0',
    }],
  });

  return { txHash, from, chain };
}
